import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { useMediaQuery } from 'react-responsive';
import { Link } from 'react-router-dom';

import { getUser } from 'data_system/token_auth/selectors';

import { Theme } from 'ui_system/theme';
import { colors, brandNeutralColors } from 'ui_system/compass/utils';
// TODO replace this icon with the one from design when we eventually add support
// for the download button
// Story: https://anyroad.atlassian.net/browse/PE-5014
// Design: https://www.sketch.com/s/c3f49704-0145-4210-b2b7-5651ef90c08a/a/jgarjVa
import DownloadIcon from 'views/ui/icons/Download';
import ContentLoader from 'ui_system/utilities/ContentLoader';
import { experiencesApi } from 'data_system/affiliate_portal/api/experiencesApi';

const Container = styled.div`
  margin: 0 auto 25px;
  padding: 0 16px;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    max-width: 900px;
    margin: 0 auto 32px;
  }
`;

const TitleBar = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 25px;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    margin-top: 32px;
  }
`;

const Header = styled.h1``;

const TitleText = styled.span`
  font-size: 28px;
  font-weight: 600;
`;

const SubTitleText = styled.span`
  font-size: 18px;
  margin-left: 8px;
`;

const DownloadButton = styled.button`
  // XXX hidden for now because "Download CSV" is not part of this story
  // https://anyroad.atlassian.net/browse/PE-5014
  visibility: hidden;

  align-items: center;
  border-radius: 3px;
  border: 1px solid ${colors.primary};
  color: ${colors.primary};
  display: flex;
  font-size: 14px;
  gap: 8px;
  padding: 9px 16px;
`;

const PayNowLink = styled(Link)`
  font-size: 14px;
  color: ${colors.primary};
  text-decoration: underline;
`;

const PayNowWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const StyledDownloadIcon = styled(DownloadIcon)`
  height: 16px;
  width: 16px;
`;

const NoEntriesWrapper = styled.div`
  border: 1px solid ${brandNeutralColors.border};
  color: ${brandNeutralColors.text.secondary};
  margin-top: 20px;
  padding: 26px 16px;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    margin-top: 24px;
  }
`;

const Grid = styled.ol`
  list-style-type: none;
  border-bottom: 1px solid ${brandNeutralColors.border};
  display: grid;
  grid-template-columns: ${(props) =>
    props.collectGuestInfo
      ? '0.75fr 2.25fr 1.25fr 0.95fr 0.95fr 0.95fr'
      : '0.75fr 3.25fr 0.5fr 0.5fr 0.75fr'};
  margin-top: 30px;
  min-width: 100%;

  li:nth-of-type(${(props) => (props.collectGuestInfo ? '6n + 1' : '5n + 1')}) {
    border-left: 1px solid ${brandNeutralColors.border};
  }

  li:nth-of-type(${(props) => (props.collectGuestInfo ? '6n + 6' : '5n + 5')}) {
    border-right: 1px solid ${brandNeutralColors.border};
  }

  li:nth-of-type(${(props) => props.collectGuestInfo && '6n + 2'}) {
    padding-right: 30px;
  }
`;

const GridCell = styled.li`
  padding: 15px;
  text-overflow: ellipsis;
`;

const HeaderCell = styled(GridCell)`
  background: ${brandNeutralColors.background.light};
  color: ${brandNeutralColors.text.primary};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
`;

const BodyCell = styled(GridCell)`
  border-top: 1px solid ${brandNeutralColors.border};
`;

const StatusLabel = styled.div`
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  color: ${brandNeutralColors.text.primary};
  display: inline-block;
  font-size: 11px;
  padding: 3px 6px;
  width: fit-content;

  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 12px;
    padding: 3px 8px;
  }
`;

const ConfirmedLabel = () => (
  <StatusLabel backgroundColor={colors.bookingStatus.confirmedLight}>
    Confirmed
  </StatusLabel>
);

const UnpaidLabel = () => (
  <StatusLabel backgroundColor="#ffddde">Unpaid</StatusLabel>
);

const CancelledLabel = () => (
  <StatusLabel backgroundColor="#e6e6e6">Cancelled</StatusLabel>
);

const PendingLabel = () => (
  <StatusLabel backgroundColor={colors.bookingStatus.pendingLight}>
    Pending
  </StatusLabel>
);

const Cards = styled.ol`
  list-style-type: none;
  margin-top: 24px;
  border-bottom: 1px solid ${brandNeutralColors.border};
`;

const CardsHeader = styled.ol`
  background: ${brandNeutralColors.background.light};
  color: ${brandNeutralColors.text.primary};
  font-size: 12px;
  font-weight: 600;
  padding: 12px 8px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const Card = styled.li`
  border: 1px solid ${brandNeutralColors.border};
  border-bottom: 0;
  font-size: 13px;
  padding: 15px 8px;

  ${StatusLabel} {
    margin-left: 7px;
  }

  > div {
    margin: 4px 0;
  }
`;

const SeparatorLine = styled.hr`
  background-color: ${brandNeutralColors.background.light};
  border: none;
  height: 1px;
  margin: 8px 0;
  width: 110px;
`;

const Price = styled.span`
  font-weight: 600;
`;

const DateTime = styled.div`
  color: ${brandNeutralColors.text.secondary};
  font-size: 11px;
  margin-top: 4px;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    font-size: 12px;
  }
`;

const BookingID = styled.div`
  margin-bottom: 10px;
`;

const NoPaymentLink = styled.div`
  margin-left: 25px;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    display: none;
  }
`;

const NameUnavailable = styled.span`
  color: ${brandNeutralColors.text.secondary};
`;

// const mockData = [
//   {
//     bookingDate: 'Apr 22, 2022',
//     bookingId: '8562950',
//     bookerName: 'Jane Smith',
//     amount: '$100.00',
//     status: 'cancelled',
//     experienceName: 'Paint Night Live: FolkArt Rustic Roses in a Crock',
//     experienceDateTime: 'Apr 22, 2022 | 1:45pm'
//   },
//   {
//     bookingDate: 'Apr 18, 2022',
//     bookingId: '9275025',
//     bookerName: 'Alexandra Montesinos',
//     amount: '$100.00',
//     status: 'pending',
//     experienceName: '',
//     experienceDateTime: 'Apr 22, 2022 | 1:45pm'
//   },
//   {
//     bookingDate: 'Apr 17, 2022',
//     bookingId: '3174561',
//     bookerName: 'Connie Harrell',
//     amount: '$9999.99',
//     status: 'confirmed',
//     experienceName: 'How to use Gelatos to create backgrounds with Faber-Castel – Premium Online',
//     experienceDateTime: 'Apr 22, 2022 | 4:00pm'
//   },
//   {
//     bookingDate: 'Apr 16, 2022',
//     bookingId: '7648223',
//     bookerName: 'Richard Crocker',
//     amount: '$100.00',
//     status: 'unpaid',
//     experienceName: 'Finisher Tour',
//     experienceDateTime: 'Apr 20, 2022 | 3:00pm'
//   }
// ];

const renderPayNowBtn = (bookingRequestId, status) => {
  if (status === 'unpaid') {
    return <PayNowLink to={`payments/${bookingRequestId}`}>Pay Now</PayNowLink>;
  }
  return <NoPaymentLink>&mdash;</NoPaymentLink>;
};

const renderLabel = (status) => {
  switch (status) {
    case 'confirmed': {
      return <ConfirmedLabel />;
    }
    case 'cancelled': {
      return <CancelledLabel />;
    }
    case 'pending': {
      return <PendingLabel />;
    }
    case 'unpaid': {
      return <UnpaidLabel />;
    }
    default: {
      return null;
    }
  }
};

const LoadingCard = ({ collectGuestInfo }) => (
  <Card>
    <div>
      <ContentLoader height="17" width="100%" />
    </div>
    <div>
      <ContentLoader height="17" width="65" />
    </div>
    <ContentLoader height="17" width="100%" />
    <ContentLoader height="17" width="100%" />
    <SeparatorLine />
    {collectGuestInfo && (
      <div>
        <ContentLoader height="17" width="164" />
      </div>
    )}
    <div>
      <Price>
        <ContentLoader height="17" width="43" />
      </Price>
      <ContentLoader height="17" width="65" />
    </div>
  </Card>
);

const MobileTable = ({ collectGuestInfo, data }) => (
  <Cards>
    <CardsHeader>Order Details</CardsHeader>

    {data.map(
      ({
        bookingDate,
        bookingId,
        bookerName,
        amount,
        status,
        experienceName,
        experienceDateTime,
        bookingRequestId,
      }) => {
        const booker = (bookerName.trim().length && bookerName) || (
          <NameUnavailable>Name Unavailable</NameUnavailable>
        );
        return (
          <Card key={`booking-${bookingId}`}>
            <PayNowWrapper>
              {bookingDate}
              {renderPayNowBtn(bookingRequestId, status)}
            </PayNowWrapper>
            <div>#{bookingId}</div>
            <div>{experienceName}</div>
            <DateTime>{experienceDateTime}</DateTime>
            <SeparatorLine />
            {collectGuestInfo && <div>{booker}</div>}
            <div>
              <Price>{amount}</Price>
              {renderLabel(status)}
            </div>
          </Card>
        );
      }
    )}
  </Cards>
);

const LoadingGridRow = ({ collectGuestInfo }) => (
  <>
    <BodyCell>
      <ContentLoader height="18" width="100%" />
    </BodyCell>
    <BodyCell>
      <ContentLoader height="18" width="100%" />
      <ContentLoader height="18" width="100%" />
      <ContentLoader height="18" width="100%" />
      <ContentLoader height="18" width="100%" />
    </BodyCell>
    {collectGuestInfo && (
      <BodyCell>
        <ContentLoader height="18" width="100%" />
      </BodyCell>
    )}
    <BodyCell>
      <ContentLoader height="18" width="100%" />
    </BodyCell>
    <BodyCell>
      <ContentLoader height="18" width="100%" />
    </BodyCell>
    <BodyCell>
      <ContentLoader height="18" width="100%" />
    </BodyCell>
  </>
);

const DesktopTable = ({ collectGuestInfo, data }) => {
  const renderDataRow = ({
    bookingDate,
    bookingId,
    bookerName,
    amount,
    status,
    experienceName,
    experienceDateTime,
    bookingRequestId,
  }) => {
    const booker = (bookerName.trim().length && bookerName) || (
      <NameUnavailable>Name Unavailable</NameUnavailable>
    );
    return (
      <React.Fragment key={`booking-${bookingId}`}>
        <BodyCell>{bookingDate}</BodyCell>
        <BodyCell>
          <BookingID>#{bookingId}</BookingID>
          <div>{experienceName}</div>
          <DateTime>{experienceDateTime}</DateTime>
        </BodyCell>
        {collectGuestInfo && <BodyCell>{booker}</BodyCell>}
        <BodyCell>{amount}</BodyCell>
        <BodyCell>{renderLabel(status)}</BodyCell>
        <BodyCell>{renderPayNowBtn(bookingRequestId, status)}</BodyCell>
      </React.Fragment>
    );
  };

  return (
    <Grid collectGuestInfo={collectGuestInfo}>
      <HeaderCell>Booking Date</HeaderCell>
      <HeaderCell>Booking</HeaderCell>
      {collectGuestInfo && <HeaderCell>Booking guest</HeaderCell>}
      <HeaderCell>Amount</HeaderCell>
      <HeaderCell>Status</HeaderCell>
      <HeaderCell>Actions</HeaderCell>

      {data.map(renderDataRow)}
    </Grid>
  );
};

const MobileLoading = ({ collectGuestInfo }) => (
  <Cards>
    {[...Array(10).keys()].map((idx) => (
      <LoadingCard key={`loading-${idx}`} collectGuestInfo={collectGuestInfo} />
    ))}
  </Cards>
);

const DesktopLoading = ({ collectGuestInfo }) => (
  <Grid collectGuestInfo={collectGuestInfo}>
    {[...Array(10).keys()].map((idx) => (
      <LoadingGridRow
        key={`loading-${idx}`}
        collectGuestInfo={collectGuestInfo}
      />
    ))}
  </Grid>
);

export const OrderHistory = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useMediaQuery({ maxWidth: Theme.mobile.phoneWidth - 1 });
  const { is_guest_info_collection_enabled } = useSelector(getUser);
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      const resp = await experiencesApi.fetchOrderHistory();
      const _data = resp.data.data.map(({ attributes }) => attributes);
      setData(_data);
      setIsLoading(false);
    };

    fetchData();
  }, []);

  const renderOrders = () => {
    if (isLoading) {
      if (isMobile) {
        return (
          <MobileLoading collectGuestInfo={is_guest_info_collection_enabled} />
        );
      }
      return (
        <DesktopLoading collectGuestInfo={is_guest_info_collection_enabled} />
      );
    }

    if (data.length === 0) {
      return <NoEntriesWrapper>No orders at this time</NoEntriesWrapper>;
    }

    if (isMobile) {
      return (
        <MobileTable
          data={data}
          collectGuestInfo={is_guest_info_collection_enabled}
        />
      );
    }

    return (
      <DesktopTable
        data={data}
        collectGuestInfo={is_guest_info_collection_enabled}
      />
    );
  };

  return (
    <Container>
      <TitleBar>
        <Header>
          <TitleText data-testid="affiliate-portal-title-orders">
            Orders
          </TitleText>
          <SubTitleText>Last 1 year</SubTitleText>
        </Header>
        {!isMobile && (
          <DownloadButton>
            <StyledDownloadIcon fill={colors.primary} />
            Download CSV
          </DownloadButton>
        )}
      </TitleBar>
      {renderOrders()}
    </Container>
  );
};
